import { useEffect, useState, useMemo } from 'react';
import { Spin, TimePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { toast, Toaster } from 'react-hot-toast';
import './Register.css';
import Navbar from './Navbar';
import {
  Briefcase,
  CheckCircle,
  Mail,
  MapPin,
  Phone,
  Upload as UploadIcon,
  User,
  X,
  Clock,
  NotepadText,
  Plus,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';
import Footer from './Footer';
import { DaysOfWeek } from '../../enums/DaysOfWeek';
import { timeOptions } from '../../enums/TimeOptions';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import TagManager from 'react-gtm-module';
import Support from '../../components/Support';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID_X = 'service_wtvsq1f';
const EMAILJS_PUBLIC_KEY_X = 'TkRNLbe4lWvlRW-1k';
const EMAILJS_X_TEMPLATE = 'template_qzpvkry';

const EMAILJS_SERVICE_ID_BOOKER = 'service_h4br5is';
const EMAILJS_PUBLIC_KEY_BOOKER = 'grg76XmeXnwqwv5cy';
const EMAILJS_TEMPLATE_BOOKER = 'template_stf18uq';

const format = 'HH:mm';
const defaultStartTime = dayjs('09:00', format);
const defaultEndTime = dayjs('17:00', format);

const timePickerStyles = {
  width: '75%',
};

const gtmId = 'GTM-T4QRDGLB';

const initializeTagManager = () => {
  TagManager.initialize({ gtmId });
};

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = useRTL();
  const [loading, setIsLoading] = useState(true);
  const [isSumbitting, setIsSubmtting] = useState(false);
  const [didSubmit, setDidSubmit] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [services, setServices] = useState([]);
  const countryCode = '+961';
  const [formData, setFormData] = useState({
    OwnerFullName: '',
    BusinessName: '',
    description: '',
    AddressDetails: '',
    PhoneNumber: '',
    Email: '',
    logo: null,
    selectedServices: [],
    workingDays: [],
    workingHours: {},
  });
  const [serviceTypesAndDetails, setServiceTypesAndDetails] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    const numbersOnly = value.replace(/[^0-9]/g, '');

    // Limit to 8 digits
    const truncated = numbersOnly.slice(0, 8);

    handleChange('PhoneNumber', truncated);
  };

  const getServices = async () => {
    const _base_url = 'api';
    try {
      const x = await fetch(
        `https://${_base_url}.thebooker.online/api/Service/dropdown`
      );
      if (x.ok) {
        setServices(await x.json());
      }
    } catch (error) {
      console.error(error);
      toast.error('An Issue Occurred! Please try Again Later', {
        duration: 4000,
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Owner Full Name validation
    if (!formData.OwnerFullName.trim()) {
      newErrors.OwnerFullName = t('register.errors.required');
    } else if (formData.OwnerFullName.trim().length < 3) {
      newErrors.OwnerFullName = t('register.errors.nameTooShort');
    }

    // Business Name validation
    if (!formData.BusinessName.trim()) {
      newErrors.BusinessName = t('register.errors.required');
    } else if (formData.BusinessName.trim().length < 2) {
      newErrors.BusinessName = t('register.errors.businessNameTooShort');
    }

    // Description validation
    // if (!formData.description.trim()) {
    //   newErrors.description = t('register.errors.required');
    // }
    // } else if (formData.description.trim().length < 20) {
    //   newErrors.description = t('register.errors.descriptionTooShort');
    // }

    // Address validation
    if (!formData.AddressDetails.trim()) {
      newErrors.AddressDetails = t('register.errors.required');
    }
    // } else if (formData.AddressDetails.trim().length < 5) {
    //   newErrors.AddressDetails = t('register.errors.addressTooShort');
    // }

    // Phone Number validation
    if (!formData.PhoneNumber.trim()) {
      newErrors.PhoneNumber = t('register.errors.required');
    } else if (!/^\d{8}$/.test(formData.PhoneNumber.trim())) {
      newErrors.PhoneNumber = t('register.errors.invalidPhone');
    } else {
      // Check if the first two digits form a valid Lebanese prefix
      const prefix = formData.PhoneNumber.trim().substring(0, 2);
      const validPrefixes = ['70', '71', '76', '78', '79', '81'];
      const validSingleDigits = ['0'];
      if (
        !validPrefixes.includes(prefix) &&
        !validSingleDigits.includes(prefix[0])
      ) {
        newErrors.PhoneNumber = t('register.errors.invalidLebanesePrefix');
      }
    }
    // Email Validation
    if (
      formData.Email.trim() !== '' &&
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        // !/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        formData.Email.trim().toLowerCase()
      )
    ) {
      newErrors.Email = t('register.errors.invalidEmail');
    }

    // Logo validation
    if (!formData.logo && !logoPreview) {
      newErrors.logo = t('register.errors.required');
    }

    // Services validation
    if (formData.selectedServices.length === 0) {
      newErrors.services = t('register.errors.selectAtLeastOneService');
    }
    // } else {
    //   // Validate service types for each selected service
    //   formData.selectedServices.forEach((serviceName) => {
    //     const types = serviceTypesAndDetails[serviceName] || [];
    //     if (types.length === 0) {
    //       newErrors[`serviceTypes_${serviceName}`] = t(
    //         'register.errors.addAtLeastOneType'
    //       );
    //     } else {
    //       types.forEach((type, index) => {
    //         if (!type.name?.trim()) {
    //           newErrors[`serviceTypeName_${serviceName}_${index}`] = t(
    //             'register.errors.typeNameRequired'
    //           );
    //         }
    //         if (!type.cost?.toString().trim()) {
    //           newErrors[`serviceTypeCost_${serviceName}_${index}`] = t(
    //             'register.errors.typeCostRequired'
    //           );
    //         } else if (isNaN(type.cost) || parseInt(type.cost) <= 0) {
    //           newErrors[`serviceTypeCost_${serviceName}_${index}`] = t(
    //             'register.errors.invalidCost'
    //           );
    //         }
    //         if (!type.duration?.toString().trim()) {
    //           newErrors[`serviceTypeDuration_${serviceName}_${index}`] = t(
    //             'register.errors.typeDurationRequired'
    //           );
    //         }
    //       });
    //     }
    //   });
    // }

    // Working Days validation
    if (formData.workingDays.length === 0) {
      newErrors.workingDays = t('register.errors.selectAtLeastOneDay');
    } else {
      // Validate that all working days are valid DaysOfWeek enum values
      const invalidDays = formData.workingDays.filter(
        (day) => !Object.values(DaysOfWeek).includes(day)
      );
      if (invalidDays.length > 0) {
        newErrors.workingDays = t('register.errors.invalidWorkingDays');
      }
    }

    // Working Hours validation
    const hasIncompleteHours = formData.workingDays.some(
      (day) =>
        !formData.workingHours[day]?.start || !formData.workingHours[day]?.end
    );

    if (hasIncompleteHours) {
      newErrors.workingHours = t('register.errors.setAllWorkingHours');
    }
    return newErrors;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024 * 2) {
        // 2MB limit
        setErrors((prev) => ({
          ...prev,
          logo: t('register.errors.fileSize'),
        }));
        return;
      }

      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({
          ...prev,
          logo: t('register.errors.fileType'),
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        logo: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Clear any existing errors
      if (errors.logo) {
        setErrors((prev) => ({
          ...prev,
          logo: '',
        }));
      }
    }
  };

  const handleServiceSelect = (service) => {
    setFormData((prev) => {
      const services = [...prev.selectedServices];
      if (services.includes(service.serviceName)) {
        setServiceTypesAndDetails((prevTypes) => {
          const updatedTypes = { ...prevTypes };
          delete updatedTypes[service.serviceName];
          return updatedTypes;
        });
        return {
          ...prev,
          selectedServices: services.filter(
            (name) => name !== service.serviceName
          ),
        };
      } else {
        setServiceTypesAndDetails((prevTypes) => ({
          ...prevTypes,
          [service.serviceName]: [{ name: '', cost: '', duration: '' }],
        }));
        return {
          ...prev,
          selectedServices: [...services, service.serviceName],
        };
      }
    });
    if (errors.services) {
      setErrors((prev) => ({
        ...prev,
        services: '',
      }));
    }
  };

  const handleServiceTypeAdd = (serviceName) => {
    setServiceTypesAndDetails((prev) => ({
      ...prev,
      [serviceName]: [
        ...(prev[serviceName] || []),
        { name: '', cost: '', duration: '' },
      ],
    }));
  };

  const handleServiceTypeChange = (serviceName, index, field, value) => {
    setServiceTypesAndDetails((prev) => ({
      ...prev,
      [serviceName]: prev[serviceName].map((type, i) =>
        i === index ? { ...type, [field]: value } : type
      ),
    }));
  };

  const handleServiceTypeRemove = (serviceName, index) => {
    setServiceTypesAndDetails((prev) => ({
      ...prev,
      [serviceName]: prev[serviceName].filter((_, i) => i !== index),
    }));
  };

  const daysOfWeek = useMemo(
    () =>
      [
        { id: DaysOfWeek.Monday, label: t('days.monday') },
        { id: DaysOfWeek.Tuesday, label: t('days.tuesday') },
        { id: DaysOfWeek.Wednesday, label: t('days.wednesday') },
        { id: DaysOfWeek.Thursday, label: t('days.thursday') },
        { id: DaysOfWeek.Friday, label: t('days.friday') },
        { id: DaysOfWeek.Saturday, label: t('days.saturday') },
        { id: DaysOfWeek.Sunday, label: t('days.sunday') },
      ].sort((a, b) => a.id - b.id),
    [t]
  );

  const handleDaySelect = (dayId) => {
    setFormData((prev) => {
      if (dayId === 'all') {
        // If all days are already selected, deselect all
        if (prev.workingDays.length === daysOfWeek.length) {
          return { ...prev, workingDays: [] };
        }
        // Otherwise, select all days and set default working hours
        const allDays = daysOfWeek.map((day) => day.id);
        const defaultWorkingHours = allDays.reduce(
          (acc, day) => ({
            ...acc,
            [day]: {
              start: defaultStartTime.format(format),
              end: defaultEndTime.format(format),
            },
          }),
          {}
        );

        return {
          ...prev,
          workingDays: allDays,
          workingHours: defaultWorkingHours,
        };
      }

      const updatedDays = prev.workingDays.includes(dayId)
        ? prev.workingDays.filter((id) => id !== dayId)
        : [...prev.workingDays, dayId];

      // If adding a new day, set default working hours for it
      const updatedWorkingHours = { ...prev.workingHours };
      if (!prev.workingDays.includes(dayId)) {
        updatedWorkingHours[dayId] = {
          start: defaultStartTime.format(format),
          end: defaultEndTime.format(format),
        };
      } else {
        // If removing a day, remove its working hours
        delete updatedWorkingHours[dayId];
      }

      return {
        ...prev,
        workingDays: updatedDays,
        workingHours: updatedWorkingHours,
      };
    });
  };

  const handleTimeChange = (dayId, timeRange) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [dayId]: {
          start: timeRange?.[0]?.format(format) || null,
          end: timeRange?.[1]?.format(format) || null,
        },
      },
    }));
  };

  const applyTimeToAllDays = (timeRange) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: prev.workingDays.reduce(
        (acc, dayId) => ({
          ...acc,
          [dayId]: {
            start: timeRange?.[0]?.format(format) || null,
            end: timeRange?.[1]?.format(format) || null,
          },
        }),
        {}
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (didSubmit || isSumbitting) return;

    validateForm();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.error(t('register.errors.checkFields'), {
        duration: 4000,
        position: 'top-center',
      });
      return;
    }

    try {
      setIsSubmtting(true);
      const _base_url = 'api';

      const selectedServiceObjects = formData.selectedServices
        .map((serviceName) => {
          const service = services.find((s) => s.serviceName === serviceName);
          return service
            ? { id: service.serviceId, name: service.serviceName }
            : null;
        })
        .filter((service) => service !== null);

      // Format service types and details in the requested structure
      const formattedServiceTypes = selectedServiceObjects.map((service) => {
        const types = serviceTypesAndDetails[service.name] || [];
        // Filter out types with empty values and map the valid ones
        return {
          serviceName: service.name,
          types: types
            .filter(
              (type) =>
                type.name?.trim() &&
                type.cost?.toString().trim() &&
                type.duration
            )
            .map((type) => ({
              name: type.name.trim(),
              cost: parseInt(type.cost.toString().trim()) || 0,
              duration: {
                label:
                  timeOptions.find((option) => option.value === type.duration)
                    ?.label || '',
                value: parseInt(type.duration) || 0,
              },
            })),
        };
      });

      // Create working hours array with the specified structure
      const workingHoursArray = formData.workingDays
        .sort((a, b) => a - b) // Sort days numerically
        .map((day) => ({
          day: day.toString(),
          times: [
            {
              start: formData.workingHours[day]?.start || '09:00',
              end: formData.workingHours[day]?.end || '17:00',
            },
          ],
        }));

      const options = {
        maxSizeMB: 1, // Max file size in MB (default: Infinity)
        maxWidthOrHeight: 1024, // Max width/height in pixels (default: undefined)
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(formData.logo, options);

      const formDataToSend = new FormData();
      formDataToSend.append('OwnerFullName', formData.OwnerFullName.trim());
      formDataToSend.append('BusinessName', formData.BusinessName.trim());
      formDataToSend.append('AddressDetails', formData.AddressDetails.trim());
      if (formData.description.trim() !== '') {
        formDataToSend.append('Description', formData.description.trim());
      }
      formDataToSend.append(
        'PhoneNumber',
        `${countryCode}${formData.PhoneNumber.trim()}`
      );
      if (formData.Email.trim() !== '') {
        formDataToSend.append('Email', formData.Email.trim());
      }
      formDataToSend.append('Services', JSON.stringify(selectedServiceObjects));
      formDataToSend.append(
        'ServiceTypesAndDetails',
        JSON.stringify(formattedServiceTypes)
      );
      formDataToSend.append('WorkingHours', JSON.stringify(workingHoursArray));
      if (formData.logo) {
        formDataToSend.append('Logo', compressedFile);
      }

      const response = await fetch(
        `https://${_base_url}.thebooker.online/api/externalBusinessRequested`,
        {
          method: 'POST',
          body: formDataToSend,
        }
      );

      const templateParams = {
        OwnerFullName: formData.OwnerFullName.trim(),
        BusinessName: formData.BusinessName.trim(),
        AddressDetails: formData.AddressDetails.trim(),
        PhoneNumber: `${countryCode}${formData.PhoneNumber.trim()}`,
        entry_date: new Date().toLocaleDateString(),
      };

      if (response.ok || response.status === 200 || response.status === 201) {
        // this Send to theBookerDev
        await emailjs
          .send(
            EMAILJS_SERVICE_ID_X,
            EMAILJS_X_TEMPLATE,
            templateParams,
            EMAILJS_PUBLIC_KEY_X
          )
          .then(
            // (response) => {
            //   console.log('X', response);
            // },
            (error) => {
              console.log('Failed to send email:', error);
            }
          );
        if (formData.Email.trim() !== '') {
          const templateParamsX = {
            OwnerFullName: formData.OwnerFullName.trim(),
            BusinessName: formData.BusinessName.trim(),
            Email: formData.Email.trim(),
            AddressDetails: formData.AddressDetails.trim(),
            PhoneNumber: `${countryCode}${formData.PhoneNumber.trim()}`,
            entry_date: new Date().toLocaleDateString(),
          };
          await emailjs
            .send(
              EMAILJS_SERVICE_ID_BOOKER,
              EMAILJS_TEMPLATE_BOOKER,
              templateParamsX,
              EMAILJS_PUBLIC_KEY_BOOKER
            )
            .then(
              // (response) => {
              //   console.log('X2', response);
              // },
              (error) => {
                console.log('Failed to send email:', error);
              }
            );
        }
        toast.success(t('register.submit.success'), {
          duration: 4000,
          position: 'top-center',
        });
        navigate('/thanksbusiness');
        setDidSubmit(true);
      } else if (response.status === 400) {
        // Show error toast for already registered
        toast.error(t('register.submit.alreadyRegistered'), {
          duration: 4000,
          position: 'top-center',
        });
      } else {
        // Show generic error toast
        toast.error(t('register.submit.error'), {
          duration: 4000,
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      toast.error(t('register.submit.error'), {
        duration: 4000,
        position: 'top-center',
      });
    } finally {
      setIsSubmtting(false);
    }
  };

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const x = params.get('lang');
    if (x === 'ar') {
      i18n.changeLanguage('ar');
      document.documentElement.dir = 'rtl';
    } else {
      i18n.changeLanguage('en');
      document.documentElement.dir = 'ltr';
    }
  }, [i18n]);

  useEffect(() => {
    document.title = 'TheBooker - Register';
    window.scrollTo(0, 0);
    getServices();
    initializeTagManager();
  }, []);

  return (
    <>
      <Support />
      <div className={`min-h-screen bg ${isRTL ? 'rtl' : 'ltr'}`}>
        <Toaster
          position={isRTL ? 'top-left' : 'top-right'}
          containerStyle={{
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        />
        <Navbar isRegisterPage={true} Translated={true} />

        <div
          className={`container mx-auto px-4 py-8 md:py-12 mt-2${
            didSubmit ? 'cursor-not-allowed opacity-60' : ''
          }`}>
          <div className='max-w-3xl mx-auto' dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <h1 className='text-3xl md:text-4xl font-hurmeBold text-center text-bookerGray mb-6'>
              {t('introKey')}
            </h1>

            {/* Intro Card */}
            <div className='bg-white rounded-lg border-t-4 border-booker shadow-sm p-6 mb-6'>
              <p className='text-gray-600'>
                {t('registerDesc')}
                <a
                  href='https://wa.me/96176624565'
                  target='_blank'
                  rel='noreferrer'
                  className='text-booker hover:text-[#007a70] underline transition-colors duration-200'>
                  {t('whatsapp')}
                </a>
                {t('registerDesc2')}
              </p>
            </div>

            {/* Form Card */}
            <div className='bg-white rounded-lg shadow-sm p-6 md:p-8'>
              <form onSubmit={handleSubmit} className='space-y-6'>
                {/* Required Fields Indicator */}
                <div className='flex justify-end mb-4'>
                  <p className='text-red-500 text-sm'>
                    {t('register.submit.requiredFields')}
                  </p>
                </div>

                {/* Full Name Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'OwnerFullName' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('OwnerFullName')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <User size={18} className='text-booker' />
                    {t('register.fullName')}{' '}
                    <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={formData.OwnerFullName}
                    onChange={(e) =>
                      handleChange('OwnerFullName', e.target.value)
                    }
                    className={`w-full p-3 border-b-2 ${
                      errors.OwnerFullName
                        ? 'border-red-500'
                        : 'border-gray-200'
                    } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                    placeholder={t('register.fullNamePlaceholder')}
                    disabled={didSubmit}
                  />
                  {errors.OwnerFullName && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.OwnerFullName}
                    </p>
                  )}
                </div>

                {/* Business Name Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'BusinessName' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('BusinessName')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <Briefcase size={18} className='text-booker' />
                    {t('register.businessName')}{' '}
                    <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={formData.BusinessName}
                    onChange={(e) =>
                      handleChange('BusinessName', e.target.value)
                    }
                    className={`w-full p-3 border-b-2 ${
                      errors.BusinessName ? 'border-red-500' : 'border-gray-200'
                    } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                    placeholder={t('register.businessNamePlaceholder')}
                    disabled={didSubmit}
                  />
                  {errors.BusinessName && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.BusinessName}
                    </p>
                  )}
                </div>
                {/* Description Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'description' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('description')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <NotepadText size={18} className='text-booker' />
                    {t('register.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    className={`w-full p-3 border-b-2 min-h-[120px] resize-y ${
                      errors.description ? 'border-red-500' : 'border-gray-200'
                    } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                    placeholder={t('register.descriptionPlaceholder')}
                    maxLength={500}
                    disabled={didSubmit}
                  />
                  <div className='mt-2 flex justify-between items-center'>
                    <div>
                      {errors.description && (
                        <p className='text-red-500 text-sm'>
                          {errors.description}
                        </p>
                      )}
                    </div>
                    <div className='text-sm text-gray-500'>
                      {formData.description.length}{' '}
                      {t('register.characterLimit')}
                    </div>
                  </div>
                </div>

                {/* Location Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'AddressDetails' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('AddressDetails')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <MapPin size={18} className='text-booker' />
                    {t('register.location')}{' '}
                    <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={formData.AddressDetails}
                    onChange={(e) =>
                      handleChange('AddressDetails', e.target.value)
                    }
                    className={`w-full p-3 border-b-2 ${
                      errors.AddressDetails
                        ? 'border-red-500'
                        : 'border-gray-200'
                    } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                    placeholder={t('register.locationPlaceholder')}
                    disabled={didSubmit}
                  />
                  {errors.AddressDetails && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.AddressDetails}
                    </p>
                  )}
                </div>

                {/* Phone Number Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'PhoneNumber' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('PhoneNumber')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <Phone size={18} className='text-booker' />
                    {t('register.phoneNumber')}{' '}
                    <span className='text-red-500'>*</span>
                  </label>
                  <div className='flex items-center gap-2 max-w-full overflow-hidden'>
                    <div className='shrink-0'>
                      <input
                        type='text'
                        value={countryCode}
                        disabled
                        className='w-16 p-3 border-b-2 border-gray-200 bg-gray-50 outline-none'
                        placeholder='+961'
                      />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <input
                        type='tel'
                        minLength={8}
                        maxLength={8}
                        value={formData.PhoneNumber}
                        disabled={didSubmit}
                        onChange={handlePhoneNumberChange}
                        onKeyDown={(e) => {
                          // Prevent any non-numeric input including 'e', '-', '+'
                          if (
                            !/[0-9]/.test(e.key) &&
                            e.key !== 'Backspace' &&
                            e.key !== 'Delete' &&
                            e.key !== 'ArrowLeft' &&
                            e.key !== 'ArrowRight' &&
                            e.key !== 'Tab'
                          ) {
                            e.preventDefault();
                          }
                        }}
                        className={`w-full p-3 border-b-2 ${
                          errors.PhoneNumber
                            ? 'border-red-500'
                            : 'border-gray-200'
                        } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                        placeholder={t('register.phoneNumberPlaceholder')}
                      />
                    </div>
                  </div>
                  {errors.PhoneNumber && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.PhoneNumber}
                    </p>
                  )}
                </div>

                {/* Email Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'email' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('email')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <Mail size={18} className='text-booker' />
                    {t('register.email')}
                  </label>
                  <input
                    type='text'
                    value={formData.Email}
                    onChange={(e) => handleChange('Email', e.target.value)}
                    className={`w-full p-3 border-b-2 ${
                      errors.Email ? 'border-red-500' : 'border-gray-200'
                    } focus:border-booker bg-transparent outline-none transition-colors duration-200`}
                    placeholder={t('register.emailPlaceholder')}
                    disabled={didSubmit}
                  />
                  {errors.Email && (
                    <p className='text-red-500 text-sm mt-2 '>{errors.Email}</p>
                  )}
                </div>

                {/* Logo Upload Field */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'logo' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('logo')}>
                  <label className='flex items-center gap-2 text-base font-medium text-gray-700 mb-3'>
                    <UploadIcon size={18} className='text-booker' />
                    {t('register.businessLogo')}{' '}
                    <span className='text-red-500'>*</span>
                  </label>

                  <div className='flex flex-col items-center justify-center'>
                    {logoPreview ? (
                      <div className='relative w-40 h-40 mb-4'>
                        <img
                          src={logoPreview}
                          alt='Business logo preview'
                          className='w-full h-full object-contain rounded-lg border border-gray-200'
                        />
                        <button
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation();
                            setLogoPreview(null);
                            setFormData((prev) => ({ ...prev, logo: null }));
                          }}
                          className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200 flex items-center justify-center'
                          disabled={didSubmit}>
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className='w-full max-w-md'>
                        <label className='flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-booker transition-colors duration-200'>
                          <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                            <UploadIcon className='w-8 h-8 mb-3 text-gray-500' />
                            <p className='mb-2 text-sm text-gray-500'>
                              <span className='font-semibold'>
                                {t('register.uploadLogo.clickToUpload')}
                              </span>{' '}
                              {t('register.uploadLogo.dragAndDrop')}
                            </p>
                            <p className='text-xs text-gray-500'>
                              {t('register.uploadLogo.fileTypes')}
                            </p>
                          </div>
                          <input
                            type='file'
                            className='hidden'
                            accept='image/png, image/jpg, image/jpeg ,image/HEIC , image/GIF'
                            onChange={handleImageChange}
                            disabled={didSubmit}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  {errors.logo && (
                    <p className='text-red-500 text-sm mt-2'>{errors.logo}</p>
                  )}
                </div>
                <h1 className='text-3xl md:text-4xl font-hurmeBold text-center text-bookerGray mb-6'>
                  {t('register.tellUsMore')}
                </h1>
                {/* Service Selection Field */}
                {loading ? (
                  <Spin className='flex justify-center' />
                ) : (
                  <div
                    className={`p-4 rounded-lg transition-all duration-200 ${
                      activeField === 'services' ? 'bg-gray-50' : ''
                    }`}
                    onClick={() => setActiveField('services')}>
                    <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2'>
                      <label className='flex items-center gap-2 text-base font-medium text-gray-700'>
                        <CheckCircle size={18} className='text-booker' />
                        {t('register.services.title')}
                        <span className='text-red-500'>*</span>
                      </label>
                      <div className='flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full'>
                        <span className='text-booker font-semibold text-lg'>
                          {formData.selectedServices.length}
                        </span>
                        <span className='text-gray-500 text-sm'>
                          {t('register.services.selected')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${
                        isRTL ? 'direction-rtl' : ''
                      }`}>
                      {services.map((service) => (
                        <div
                          key={service.serviceId}
                          onClick={() =>
                            didSubmit ? null : handleServiceSelect(service)
                          }
                          className={`relative cursor-pointer rounded-lg p-3 border-2 transition-all duration-200 ${
                            formData.selectedServices.includes(
                              service.serviceName
                            )
                              ? 'border-booker bg-booker/5'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${
                            didSubmit ? 'opacity-70 cursor-not-allowed' : ''
                          }`}>
                          <div className='flex items-center gap-3'>
                            <img
                              src={service.untitledImageUrl}
                              alt={service.serviceName}
                              className='w-12 h-12 object-contain'
                            />
                            <p className='text-sm font-medium flex-1'>
                              {service.serviceName}
                            </p>
                            {formData.selectedServices.includes(
                              service.serviceName
                            ) && (
                              <div className='bg-booker rounded-full p-1 flex items-center justify-center'>
                                <CheckCircle size={16} className='text-white' />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.services && (
                      <p className='text-red-500 text-sm mt-2'>
                        {errors.services}
                      </p>
                    )}
                  </div>
                )}

                {/* Update the service types section with logo and validation */}
                {formData.selectedServices.length > 0 && (
                  <div className='mt-6 space-y-6'>
                    <div className='text-lg font-medium text-gray-700 mb-4 flex items-center gap-2'>
                      <CheckCircle size={18} className='text-booker' />
                      {t('register.services.serviceTypes')}
                    </div>

                    {formData.selectedServices.map((serviceName) => {
                      const service = services.find(
                        (s) => s.serviceName === serviceName
                      );
                      return (
                        <div
                          key={serviceName}
                          className={`p-4 border border-gray-200 rounded-lg ${
                            isRTL ? 'rtl' : 'ltr'
                          }`}>
                          <div className='flex items-center justify-between mb-4'>
                            <div className='flex items-center gap-3'>
                              <img
                                src={service?.untitledImageUrl}
                                alt={service?.serviceName}
                                className='w-12 h-12 object-contain rounded-lg'
                              />
                              <div>
                                <h3 className='font-medium text-gray-800'>
                                  {service?.serviceName}
                                </h3>
                                <p className='text-sm text-gray-500'>
                                  {serviceTypesAndDetails[serviceName]
                                    ?.length || 0}{' '}
                                  {serviceTypesAndDetails[serviceName]
                                    ?.length === 1
                                    ? t('register.typeAdded')
                                    : t('register.typesAdded')}
                                </p>
                              </div>
                            </div>
                            <button
                              type='button'
                              onClick={() => handleServiceTypeAdd(serviceName)}
                              disabled={didSubmit}
                              className={`whitespace-nowrap text-sm text-booker hover:text-[#007a70] flex items-center gap-1 px-2 py-1.5 border border-booker rounded-md hover:bg-booker/5 transition-all duration-200 ${
                                isRTL ? 'flex-row-reverse' : ''
                              } ${
                                didSubmit ? 'opacity-50 cursor-not-allowed' : ''
                              }`}>
                              <Plus size={14} />
                              {t('register.addType')}
                            </button>
                          </div>

                          <div className='space-y-4'>
                            {(serviceTypesAndDetails[serviceName] || []).map(
                              (type, index) => (
                                <div
                                  key={index}
                                  className='p-4 bg-gray-50 rounded-lg border border-gray-200 relative'>
                                  <div className='flex-1 space-y-4'>
                                    <div className='flex items-center gap-2'>
                                      <span className='text-sm font-medium text-gray-500'>
                                        {t('register.type')} {index + 1}
                                      </span>
                                      <button
                                        type='button'
                                        onClick={() =>
                                          handleServiceTypeRemove(
                                            serviceName,
                                            index
                                          )
                                        }
                                        disabled={didSubmit}
                                        className={`absolute ${
                                          isRTL ? 'left-4' : 'right-4'
                                        } top-4 p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200 ${
                                          didSubmit
                                            ? 'opacity-50 cursor-not-allowed'
                                            : ''
                                        }`}>
                                        <X size={16} />
                                      </button>
                                    </div>
                                    <input
                                      type='text'
                                      value={type.name}
                                      onChange={(e) =>
                                        handleServiceTypeChange(
                                          serviceName,
                                          index,
                                          'name',
                                          e.target.value
                                        )
                                      }
                                      disabled={didSubmit}
                                      placeholder={t(
                                        'register.typeNamePlaceholder'
                                      )}
                                      className={`w-full p-2 border-b ${
                                        errors[
                                          `serviceTypeName_${serviceName}_${index}`
                                        ]
                                          ? 'border-red-500'
                                          : 'border-gray-200'
                                      } focus:border-booker outline-none bg-transparent ${
                                        isRTL ? 'text-right' : 'text-left'
                                      }`}
                                    />
                                    {errors[
                                      `serviceTypeName_${serviceName}_${index}`
                                    ] && (
                                      <p className='text-red-500 text-sm'>
                                        {
                                          errors[
                                            `serviceTypeName_${serviceName}_${index}`
                                          ]
                                        }
                                      </p>
                                    )}
                                    <div
                                      className={`flex gap-4 ${
                                        isRTL ? 'flex-row-reverse' : ''
                                      }`}>
                                      <div className='flex-1'>
                                        <label
                                          className={`text-xs text-gray-500 mb-1 block ${
                                            isRTL ? 'text-right' : 'text-left'
                                          }`}>
                                          {t('register.cost')}
                                        </label>
                                        <input
                                          type='tel'
                                          inputMode='numeric'
                                          pattern='[0-9]*'
                                          value={type.cost}
                                          onChange={(e) =>
                                            handleServiceTypeChange(
                                              serviceName,
                                              index,
                                              'cost',
                                              e.target.value
                                            )
                                          }
                                          disabled={didSubmit}
                                          onKeyDown={(e) => {
                                            // Prevent any non-numeric input including 'e', '-', '+'
                                            if (
                                              !/[0-9]/.test(e.key) &&
                                              e.key !== 'Backspace' &&
                                              e.key !== 'Delete' &&
                                              e.key !== 'ArrowLeft' &&
                                              e.key !== 'ArrowRight' &&
                                              e.key !== 'Tab'
                                            ) {
                                              e.preventDefault();
                                            }
                                          }}
                                          placeholder={t(
                                            'register.costPlaceholder'
                                          )}
                                          min='0'
                                          className={`w-full p-2 border-b ${
                                            errors[
                                              `serviceTypeCost_${serviceName}_${index}`
                                            ]
                                              ? 'border-red-500'
                                              : 'border-gray-200'
                                          } focus:border-booker outline-none bg-transparent ${
                                            isRTL ? 'text-right' : 'text-left'
                                          }`}
                                        />
                                        {errors[
                                          `serviceTypeCost_${serviceName}_${index}`
                                        ] && (
                                          <p className='text-red-500 text-sm mt-1'>
                                            {
                                              errors[
                                                `serviceTypeCost_${serviceName}_${index}`
                                              ]
                                            }
                                          </p>
                                        )}
                                      </div>
                                      <div className='flex-1'>
                                        <label
                                          className={`text-xs text-gray-500 mb-1 block ${
                                            isRTL ? 'text-right' : 'text-left'
                                          }`}>
                                          {t('register.duration')}
                                        </label>
                                        <Select
                                          value={type.duration || undefined}
                                          onChange={(value) =>
                                            handleServiceTypeChange(
                                              serviceName,
                                              index,
                                              'duration',
                                              value
                                            )
                                          }
                                          disabled={didSubmit}
                                          virtual={false}
                                          placement='bottomLeft'
                                          placeholder={t(
                                            'register.selectDuration'
                                          )}
                                          popupClassName='duration-select-dropdown'
                                          listHeight={256}
                                          className={`w-full ${
                                            errors[
                                              `serviceTypeDuration_${serviceName}_${index}`
                                            ]
                                              ? 'select-error'
                                              : ''
                                          }`}
                                          options={timeOptions}
                                          optionFilterProp='label'
                                          showSearch={false}
                                          dropdownStyle={{ minWidth: '200px' }}
                                        />
                                        {errors[
                                          `serviceTypeDuration_${serviceName}_${index}`
                                        ] && (
                                          <p className='text-red-500 text-sm'>
                                            {
                                              errors[
                                                `serviceTypeDuration_${serviceName}_${index}`
                                              ]
                                            }
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>

                          {serviceTypesAndDetails[serviceName]?.length ===
                            0 && (
                            <div className='text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300'>
                              <p className='text-sm text-gray-500'>
                                {t('register.addFirstTypeMessage')}
                              </p>
                              {errors[`serviceTypes_${serviceName}`] && (
                                <p className='text-red-500 text-sm mt-2'>
                                  {errors[`serviceTypes_${serviceName}`]}
                                </p>
                              )}
                              <button
                                type='button'
                                onClick={() =>
                                  handleServiceTypeAdd(serviceName)
                                }
                                disabled={didSubmit}
                                className={`mt-2 text-sm text-booker hover:text-[#007a70] flex items-center gap-1 mx-auto ${
                                  isRTL ? 'flex-row-reverse' : ''
                                }`}>
                                <Plus size={16} />
                                {t('register.addFirstType')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Working Days Selection */}
                <div
                  className={`p-4 rounded-lg transition-all duration-200 ${
                    activeField === 'workingDays' ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => setActiveField('workingDays')}>
                  <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2'>
                    <label className='flex items-center gap-2 text-base font-medium text-gray-700'>
                      <Clock size={18} className='text-booker' />
                      {t('register.workingHours.title')}{' '}
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full'>
                      <span className='text-booker font-semibold text-lg'>
                        {formData.workingDays.length}
                      </span>
                      <span className='text-gray-500 text-sm'>
                        {t('register.daysSelected')}
                      </span>
                    </div>
                  </div>

                  <div className='space-y-3'>
                    {/* Select All Button */}
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDaySelect('all');
                      }}
                      disabled={didSubmit}
                      className={`w-full p-2 text-sm font-medium text-booker border-2 border-dashed border-booker rounded-lg hover:bg-booker/5 transition-colors duration-200 ${
                        didSubmit ? 'opacity-50 cursor-not-allowed' : ''
                      }`}>
                      {formData.workingDays.length === daysOfWeek.length
                        ? t('register.Deselect_All')
                        : t('register.Select_All_Days')}
                    </button>

                    {/* Add Time Apply to All Section */}
                    {formData.workingDays.length > 1 && (
                      <div className='p-4 bg-gray-50 rounded-lg border border-gray-200'>
                        <div className='flex flex-col sm:flex-row items-center gap-3'>
                          <span className='text-sm font-medium text-gray-700 whitespace-nowrap'>
                            {t('register.workingHours.setTimeForAll')}
                          </span>
                          <TimePicker.RangePicker
                            format={format}
                            onChange={(times) => applyTimeToAllDays(times)}
                            disabled={didSubmit}
                            className='w-full max-w-[240px]'
                            style={timePickerStyles}
                            placeholder={[t('start'), t('End')]}
                            minuteStep={5}
                            placement='bottomLeft'
                            showNow={false}
                            use12Hours={false}
                            popupClassName='working-hours-popup ltr'
                            defaultValue={[defaultStartTime, defaultEndTime]}
                            value={[defaultStartTime, defaultEndTime]}
                            allowClear={false}
                            inputReadOnly={true}
                            showTime={{
                              hideDisabledOptions: true,
                              defaultValue: [
                                dayjs('09:00', format),
                                dayjs('17:00', format),
                              ],
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Days Grid with Time Selection */}
                    <div className='space-y-3'>
                      {daysOfWeek.map((day) => (
                        <div
                          key={day.id}
                          className={`rounded-lg border-2 transition-all duration-200 ${
                            formData.workingDays.includes(day.id)
                              ? 'border-booker'
                              : 'border-gray-200'
                          }`}>
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDaySelect(day.id);
                            }}
                            className={`cursor-pointer p-3 flex items-center justify-between ${
                              formData.workingDays.includes(day.id)
                                ? 'bg-booker/5'
                                : ''
                            }`}>
                            <span className='text-sm font-medium'>
                              {day.label}
                            </span>
                            <CheckCircle
                              size={16}
                              className={`${
                                formData.workingDays.includes(day.id)
                                  ? 'text-booker'
                                  : 'text-gray-300'
                              }`}
                            />
                          </div>

                          <div
                            className={`overflow-hidden transition-all duration-200 ${
                              formData.workingDays.includes(day.id)
                                ? 'h-16'
                                : 'h-0'
                            }`}>
                            <div className='p-3 border-t border-gray-200 bg-gray-50'>
                              <div className='flex items-center justify-center'>
                                <TimePicker.RangePicker
                                  format={format}
                                  value={[
                                    formData.workingHours[day.id]?.start
                                      ? dayjs(
                                          formData.workingHours[day.id].start,
                                          format
                                        )
                                      : defaultStartTime,
                                    formData.workingHours[day.id]?.end
                                      ? dayjs(
                                          formData.workingHours[day.id].end,
                                          format
                                        )
                                      : defaultEndTime,
                                  ]}
                                  onChange={(times) =>
                                    handleTimeChange(day.id, times)
                                  }
                                  disabled={didSubmit}
                                  className='w-full max-w-[240px]'
                                  style={timePickerStyles}
                                  placeholder={[t('start'), t('End')]}
                                  minuteStep={5}
                                  placement='bottomLeft'
                                  showNow={false}
                                  use12Hours={false}
                                  popupClassName='working-hours-popup ltr'
                                  defaultValue={[
                                    defaultStartTime,
                                    defaultEndTime,
                                  ]}
                                  allowClear={false}
                                  inputReadOnly={true}
                                  showTime={{
                                    hideDisabledOptions: true,
                                    defaultValue: [
                                      dayjs('09:00', format),
                                      dayjs('17:00', format),
                                    ],
                                    preventScroll: true, // Add this line
                                    disableScroll: true, // Add this line
                                  }}
                                  onSelect={(time) => {
                                    // Prevent default scroll behavior
                                    if (
                                      document.activeElement instanceof
                                      HTMLElement
                                    ) {
                                      document.activeElement.blur();
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {errors.workingDays && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.workingDays}
                    </p>
                  )}
                  {errors.workingHours && (
                    <p className='text-red-500 text-sm mt-2'>
                      {errors.workingHours}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div className='mt-8 flex flex-col sm:flex-row justify-between items-center gap-4'>
                  <button
                    type='submit'
                    disabled={isSumbitting}
                    className={`w-full sm:w-auto bg-booker text-white px-6 py-3 rounded-md font-semibold transition-colors duration-200 flex items-center justify-center gap-2 ${
                      isSumbitting
                        ? 'bg-bookerGray cursor-not-allowed'
                        : 'hover:bg-[#007a70]'
                    } ${didSubmit ? 'opacity-70 cursor-not-allowed' : ''} `}>
                    {didSubmit ? (
                      <>
                        <CheckCircle size={18} />
                        {t('register.submit.done')}
                      </>
                    ) : isSumbitting ? (
                      <>
                        <Loader2 className='w-5 h-5 animate-spin' />
                        {t('register.submit.loading')}
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        {t('register.submit.button')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Register;
